import type { FallbackProps } from "react-error-boundary";

import { ErrorModal } from "components/ui/ErrorModal";
import { useIsOpenState } from "hooks/useIsOpenState";
import { useNormalizedError } from "hooks/useNormalizeError";
import { useCallback, useEffect } from "react";
import { EntitleError } from "utils/errors/entitleError";
import NewVersionError from "utils/errors/newVersionError";
import { logrocketLogError } from "utils/logrocket";
import { reloadOnNewVersion } from "utils/version";

export const DefaultErrorBoundary: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const { close, isOpen, open } = useIsOpenState();

  const closeErrorBoundary = useCallback(() => {
    resetErrorBoundary();
    close();
  }, [close, resetErrorBoundary]);

  const normalizedError = useNormalizedError(error);

  useEffect(() => {
    if (normalizedError instanceof NewVersionError) {
      const didReload = reloadOnNewVersion();
      if (didReload) return;
    }
    if (normalizedError && !isOpen) open();
  }, [normalizedError, isOpen, open]);

  useEffect(() => {
    if (!normalizedError) return;
    logrocketLogError(normalizedError, { location: "ErrorBoundary" });
  }, [normalizedError]);

  if (error instanceof EntitleError && error.suppressError) {
    return null;
  }
  return <ErrorModal error={normalizedError} isOpen={isOpen} onClose={closeErrorBoundary} useUnwrapped />;
};
